/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';

/** LocalStorage helpers (scoped to this component) */
const DEFAULT_KEY = 'pcdl.promo.dismissed';
const DEFAULT_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

function isDismissed(storageKey = DEFAULT_KEY, ttl = DEFAULT_TTL) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return false;
    const { ts } = JSON.parse(raw);
    return Date.now() - Number(ts || 0) < ttl;
  } catch {
    return false;
  }
}

function rememberDismiss(storageKey = DEFAULT_KEY) {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ ts: Date.now() }));
  } catch {
    /* ignore */
  }
}

/**
 * PromoBanner
 * Fixed under a 64px navbar (top-16). Full-bleed background, constrained inner content.
 */
export default function PromoBanner({
  open,
  onClose,
  title = '4 Months Of Transformation',
  message = "It's Time to Move to the Advanced Level",
  ctaLabel = 'Participate',
  to = '/4mot',
  storageKey = DEFAULT_KEY,
  ttl = DEFAULT_TTL,
  remember = true,
}: {
  open: boolean;
  onClose?: () => void;
  title?: string;
  message?: string;
  ctaLabel?: string;
  to?: string;
  storageKey?: string;
  ttl?: number;
  remember?: boolean;
}) {
  const show = Boolean(open) && !isDismissed(storageKey, ttl);

  const handleDismiss = () => {
    if (remember) rememberDismiss(storageKey);
    onClose?.();
  };

  return (
    <Transition
      appear
      show={show}
      enter="transition transform ease-out duration-300"
      enterFrom="-translate-y-full opacity-0"
      enterTo="translate-y-0 opacity-100"
      leave="transition transform ease-in duration-200"
      leaveFrom="translate-y-0 opacity-100"
      leaveTo="-translate-y-full opacity-0"
    >
      {/* Full-width bar fixed beneath navbar. */}
      <div className="pointer-events-none fixed left-0 right-0 top-16 z-[45]">
        {/* Full-bleed background container (NOT width-limited) */}
        <div className="relative isolate overflow-hidden bg-gray-50 py-2.5">
          {/* Abstract gradient blobs (background only) */}
          <div
            aria-hidden="true"
            className="absolute top-1/2 left-[max(-7rem,calc(50%-52rem))] -z-10 -translate-y-1/2 transform-gpu blur-2xl"
          >
            <div
              style={{
                clipPath:
                  'polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)',
              }}
              className="w-[36rem] aspect-[577/310] bg-gradient-to-r from-[#ff80b5] to-[#9089fc] opacity-30"
            />
          </div>
          <div
            aria-hidden="true"
            className="absolute top-1/2 left-[max(45rem,calc(50%+8rem))] -z-10 -translate-y-1/2 transform-gpu blur-2xl"
          >
            <div
              style={{
                clipPath:
                  'polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)',
              }}
              className="w-[36rem] aspect-[577/310] bg-gradient-to-r from-[#ff80b5] to-[#9089fc] opacity-30"
            />
          </div>

          {/* Width-constrained content row */}
          <div className="pointer-events-auto mx-auto flex max-w-7xl items-center gap-x-4 sm:gap-x-6 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <p className="text-sm/6 text-gray-900">
                <strong className="font-semibold">{title}</strong>
                <svg
                  viewBox="0 0 2 2"
                  aria-hidden="true"
                  className="mx-2 inline size-0.5 fill-current"
                >
                  <circle r={1} cx={1} cy={1} />
                </svg>
                {message}
              </p>
              <Link
              onClick={handleDismiss}
                to={to}
                className="flex-none rounded-full bg-gray-900 px-3.5 py-1 text-sm font-semibold text-white shadow
                           hover:bg-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
              >
                {ctaLabel} <span aria-hidden="true">→</span>
              </Link>
            </div>

            {/* Dismiss button aligned to the right of the constrained row */}
            <div className="ml-auto">
              <button
                type="button"
                onClick={handleDismiss}
                className="-m-3 p-3 focus-visible:-outline-offset-4"
                aria-label="Dismiss"
                title="Dismiss"
              >
                <XMarkIcon aria-hidden="true" className="size-5 text-gray-900" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
}
